import React, { lazy }  from 'react';
import { Route, Navigate } from 'react-router-dom';
import { PageRoute } from '../types/navigation';
import useAuth from '../hooks/useAuth';
const Dashboard = lazy(() => import('../pages/Dashboard').then(module => ({ default: module.Dashboard })));
const MyClasses = lazy(() => import('../pages/MyClasses'));
const MyClassList = lazy(() => import('../pages/MyClassList'));
const ClassDetails = lazy(() => import('../pages/ClassDetails'));
const EnterScores = lazy(() => import('../pages/EnterScores'));
const Attendance = lazy(() => import('../pages/Attendance'));
const AttendanceRegister = lazy(() => import('../pages/AttendanceRegister'));
const Reports = lazy(() => import('../pages/Reports'));
const ManageTeachers = lazy(() => import('../pages/ManageTeachers'));
const ManageSubjects = lazy(() => import('../pages/ManageSubjects'));
const ManageClasses = lazy(() => import('../pages/ManageClasses'));
const Profile = lazy(() => import('../pages/Profile'));
const SuperadminAnalytics = lazy(() => import('../pages/SuperadminAnalytics'));
const SuperadminDashboardGrid = lazy(() => import('../pages/SuperadminDashboardGrid'));
const SuperadminTenants = lazy(() => import('../pages/SuperadminTenants'));
const SuperadminUsers = lazy(() => import('../pages/SuperadminUsers'));
const SuperadminAuditLogs = lazy(() => import('../pages/SuperadminAuditLogs'));
const SuperadminSettings = lazy(() => import('../pages/SuperadminSettings'));
const SuperadminPasswordResets = lazy(() => import('../pages/SuperadminPasswordResets'));
const Login = lazy(() => import('../pages/Login'));
const ManageStudents = lazy(() => import('../pages/ManageStudents'));
const StudentProfile = lazy(() => import('../pages/StudentProfile'));
const FeaturePlaceholder = lazy(() => import('../pages/FeaturePlaceholder'));
const AssignSubjects = lazy(() => import('../pages/AssignSubjects'));
const AssignPupils = lazy(() => import('../pages/AssignPupils'));
const Settings = lazy(() => import('../pages/Settings'));

const ReportsLayout = lazy(() => import('../components/Layout/ReportsLayout'));
const MarkSchedule = lazy(() => import('../pages/reports/MarkSchedule'));
const GeneralAnalysis = lazy(() => import('../pages/reports/GeneralAnalysis'));
const SubjectAnalysis = lazy(() => import('../pages/reports/SubjectAnalysis'));
const ReportCards = lazy(() => import('../pages/reports/ReportCards'));

export const PUBLIC_ROUTES: PageRoute[] = [
  {
    path: '/login',
    element: <Login />,
    label: 'Login',
    roles: [],
    breadcrumb: ['Login'],
    layout: 'public',
  },
];

export const SUPERADMIN_ROUTES: PageRoute[] = [
  {
    path: '/superadmin/dashboard',
    element: <SuperadminDashboardGrid />,
    label: 'Dashboard',
    roles: ['super_admin'],
    breadcrumb: ['Dashboard'],
    layout: 'superadmin',
  },
  {
    path: '/superadmin/tenants',
    element: <SuperadminTenants />,
    label: 'Schools/Tenants',
    roles: ['super_admin'],
    breadcrumb: ['Schools/Tenants'],
    layout: 'superadmin',
  },
  {
    path: '/superadmin/tenants/new',
    element: <FeaturePlaceholder title="Create School" subtitle="Add a new school to the system." />,
    label: 'Create School',
    roles: ['super_admin'],
    breadcrumb: ['Schools/Tenants', 'Create'],
    layout: 'superadmin',
  },
  {
    path: '/superadmin/tenants/:tenantId',
    element: <FeaturePlaceholder title="School Details" subtitle="View and edit school details and limits." />,
    label: 'School Details',
    roles: ['super_admin'],
    breadcrumb: ['Schools/Tenants', 'Details'],
    layout: 'superadmin',
  },
  {
    path: '/superadmin/tenants/:tenantId/subscription',
    element: <FeaturePlaceholder title="School Subscription Management" subtitle="Manage billing plans and active school terms." />,
    label: 'Subscription',
    roles: ['super_admin'],
    breadcrumb: ['Schools/Tenants', 'Subscription'],
    layout: 'superadmin',
  },
  {
    path: '/superadmin/tenants/:tenantId/logs',
    element: <FeaturePlaceholder title="School Activity Logs" subtitle="See actions and changes made by users in this school." />,
    label: 'Activity Logs',
    roles: ['super_admin'],
    breadcrumb: ['Schools/Tenants', 'Logs'],
    layout: 'superadmin',
  },
  {
    path: '/superadmin/users',
    element: <SuperadminUsers />,
    label: 'Platform Users',
    roles: ['super_admin'],
    breadcrumb: ['Users'],
    layout: 'superadmin',
  },

  {
    path: '/superadmin/analytics',
    element: <SuperadminAnalytics />,
    label: 'Analytics',
    roles: ['super_admin'],
    breadcrumb: ['Analytics'],
    layout: 'superadmin',
  },

  {
    path: '/superadmin/support',
    element: <SuperadminPasswordResets />,
    label: 'Password Resets',
    roles: ['super_admin'],
    breadcrumb: ['Password Resets'],
    layout: 'superadmin',
  },

  {
    path: '/superadmin/settings',
    element: <SuperadminSettings />,
    label: 'System Settings',
    roles: ['super_admin'],
    breadcrumb: ['System Settings'],
    layout: 'superadmin',
  },

  {
    path: '/superadmin/audit-logs',
    element: <SuperadminAuditLogs />,
    label: 'Audit Logs',
    roles: ['super_admin'],
    breadcrumb: ['Audit Logs'],
    layout: 'superadmin',
  },
  {
    path: '/superadmin/roles',
    element: <FeaturePlaceholder title="Roles List" subtitle="Manage user roles and system access permissions." />,
    label: 'Roles & Permissions',
    roles: ['super_admin'],
    breadcrumb: ['Roles & Permissions'],
    layout: 'superadmin',
  },
  {
    path: '/superadmin/roles/new',
    element: <FeaturePlaceholder title="Create Role" subtitle="Add a new customized user role." />,
    label: 'Create Role',
    roles: ['super_admin'],
    breadcrumb: ['Roles & Permissions', 'Create'],
    layout: 'superadmin',
  },
  {
    path: '/superadmin/roles/:roleId/permissions',
    element: <FeaturePlaceholder title="Permission Assignment" subtitle="Choose what sections and actions this role can access." />,
    label: 'Assign Permissions',
    roles: ['super_admin'],
    breadcrumb: ['Roles & Permissions', 'Assign'],
    layout: 'superadmin',
  },
  {
    path: '/superadmin/permissions/templates',
    element: <FeaturePlaceholder title="System Permission Templates" subtitle="View pre-made permission configurations." />,
    label: 'Permission Templates',
    roles: ['super_admin'],
    breadcrumb: ['Permissions', 'Templates'],
    layout: 'superadmin',
  },
  {
    path: '/superadmin/profile',
    element: <FeaturePlaceholder title="Profile" subtitle="Change your account info, notification settings, and password." />,
    label: 'Profile',
    roles: ['super_admin'],
    breadcrumb: ['Profile'],
    layout: 'superadmin',
  },
];

export const PROTECTED_ROUTES: PageRoute[] = [
  {
    path: '/dashboard',
    element: <Dashboard />,
    label: 'Dashboard',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'teacher', 'class_teacher'],
    breadcrumb: ['Dashboard'],
    layout: 'protected',
  },
  {
    path: '/students',
    element: <ManageStudents />,
    label: 'Students',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Students'],
    layout: 'protected',
  },
  {
    path: '/students/new',
    element: <FeaturePlaceholder title="Add Student" subtitle="Register a new student and place them in a class." />,
    label: 'Add Student',
    roles: ['school_admin'],
    breadcrumb: ['Students', 'Add Student'],
    layout: 'protected',
  },
  {
    path: '/students/:studentId',
    element: <StudentProfile />,
    label: 'Student Profile',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher'],
    breadcrumb: ['Students', 'Profile'],
    layout: 'protected',
  },
  {
    path: '/students/:studentId/history',
    element: <FeaturePlaceholder title="Academic History" subtitle="View past student grades and history." />,
    label: 'Academic History',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Students', 'History'],
    layout: 'protected',
  },
  {
    path: '/promotion',
    element: <FeaturePlaceholder title="Promotion Management" subtitle="Move students to their next classes at the end of the year." />,
    label: 'Promotion',
    roles: ['school_admin'],
    breadcrumb: ['Students', 'Promotion'],
    layout: 'protected',
  },
  {
    path: '/transfers',
    element: <FeaturePlaceholder title="Transfer Management" subtitle="Manage students transferring to or from other schools." />,
    label: 'Transfers',
    roles: ['school_admin'],
    breadcrumb: ['Students', 'Transfers'],
    layout: 'protected',
  },
  {
    path: '/students/graduated',
    element: <FeaturePlaceholder title="Graduated Students" subtitle="View students who have graduated from the school." />,
    label: 'Graduated Students',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Students', 'Graduated'],
    layout: 'protected',
  },
  {
    path: '/students/withdrawn',
    element: <FeaturePlaceholder title="Withdrawn Students" subtitle="View students who left the school before completing." />,
    label: 'Withdrawn Students',
    roles: ['school_admin'],
    breadcrumb: ['Students', 'Withdrawn'],
    layout: 'protected',
  },
  {
    path: '/teachers',
    element: <ManageTeachers />,
    label: 'Teachers',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Teachers'],
    layout: 'protected',
  },
  {
    path: '/teachers/new',
    element: <FeaturePlaceholder title="Add Teacher" subtitle="Register a new teacher and assign them classes." />,
    label: 'Add Teacher',
    roles: ['school_admin'],
    breadcrumb: ['Teachers', 'Add Teacher'],
    layout: 'protected',
  },
  {
    path: '/teachers/:teacherId',
    element: <FeaturePlaceholder title="Teacher Profile" subtitle="View teacher details, schedule, and assigned classes." />,
    label: 'Teacher Profile',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Teachers', 'Profile'],
    layout: 'protected',
  },
  {
    path: '/teachers/assignments',
    element: <FeaturePlaceholder title="Teacher Assignments" subtitle="View teachers assigned to each class and subject." />,
    label: 'Teacher Assignments',
    roles: ['school_admin'],
    breadcrumb: ['Teachers', 'Assignments'],
    layout: 'protected',
  },
  {
    path: '/teachers/performance',
    element: <FeaturePlaceholder title="Teacher Performance" subtitle="Compare student marks across different teachers." />,
    label: 'Teacher Performance',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Teachers', 'Performance'],
    layout: 'protected',
  },
  {
    path: '/teachers/workload',
    element: <FeaturePlaceholder title="Workload Overview" subtitle="View teachers\' teaching hours and schedules." />,
    label: 'Workload Overview',
    roles: ['school_admin'],
    breadcrumb: ['Teachers', 'Workload'],
    layout: 'protected',
  },

  {
    path: '/manage-classes',
    element: <ManageClasses />,
    label: 'Manage Classes',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Classes', 'Manage'],
    layout: 'protected',
  },
  {
    path: '/classes',
    element: <MyClasses />,
    label: 'Manage Assessments',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Manage Assessments'],
    layout: 'protected',
  },
  {
    path: '/my-class-list',
    element: <MyClassList />,
    label: 'My Class List',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['My Class List'],
    layout: 'protected',
  },
  {
    path: '/classes/new',
    element: <FeaturePlaceholder title="Create Class" subtitle="Add a new class and assign its class teacher." />,
    label: 'Create Class',
    roles: ['school_admin'],
    breadcrumb: ['Classes', 'Create Class'],
    layout: 'protected',
  },
  {
    path: '/classes/:classId',
    element: <ClassDetails />,
    label: 'My Class List',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Manage Assessments', 'My Class List'],
    layout: 'protected',
  },
  {
    path: '/reports',
    element: <Navigate to="/reports/schedule" replace />,
    label: 'Reports',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Reports'],
    layout: 'protected',
  },
  {
    path: '/reports/schedule',
    element: <ReportsLayout><MarkSchedule /></ReportsLayout>,
    label: 'Mark Schedule',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Reports', 'Mark Schedule'],
    layout: 'protected',
  },
  {
    path: '/reports/general',
    element: <ReportsLayout><GeneralAnalysis /></ReportsLayout>,
    label: 'General Analysis',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Reports', 'General Analysis'],
    layout: 'protected',
  },
  {
    path: '/reports/subject',
    element: <ReportsLayout><SubjectAnalysis /></ReportsLayout>,
    label: 'Subject Analysis',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Reports', 'Subject Analysis'],
    layout: 'protected',
  },
  {
    path: '/reports/cards',
    element: <ReportsLayout><ReportCards /></ReportsLayout>,
    label: 'Report Cards',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Reports', 'Report Cards'],
    layout: 'protected',
  },
  {
    path: '/classes/:classId/students',
    element: <FeaturePlaceholder title="Class Student List" subtitle="View the list of students registered in this class." />,
    label: 'Class Students',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Classes', 'Students'],
    layout: 'protected',
  },
  {
    path: '/classes/:classId/assign-teacher',
    element: <FeaturePlaceholder title="Assign Class Teacher" subtitle="Assign a class teacher to this class." />,
    label: 'Assign Class Teacher',
    roles: ['school_admin'],
    breadcrumb: ['Classes', 'Assign Class Teacher'],
    layout: 'protected',
  },
  {
    path: '/classes/:classId/performance',
    element: <FeaturePlaceholder title="Class Performance" subtitle="View average grades and marks for this class." />,
    label: 'Class Performance',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Classes', 'Performance'],
    layout: 'protected',
  },
  {
    path: '/classes/:classId/grade',
    element: <EnterScores />,
    label: 'Result Entry',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'teacher', 'class_teacher'],
    permissions: ['manage_results'],
    breadcrumb: ['Classes', 'Result Entry'],
    layout: 'protected',
  },
  {
    path: '/classes/:classId/attendance',
    element: <Attendance />,
    label: 'Attendance',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Classes', 'Attendance'],
    layout: 'protected',
  },
  {
    path: '/attendance',
    element: <AttendanceRegister />,
    label: 'Mark Register',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Attendance'],
    layout: 'protected',
  },
  {
    path: '/subjects',
    element: <ManageSubjects />,
    label: 'Subjects',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'teacher'],
    breadcrumb: ['Subjects'],
    layout: 'protected',
  },
  {
    path: '/assign-subjects',
    element: <AssignSubjects />,
    label: 'Assign Subjects',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Subjects', 'Assign to Classes'],
    layout: 'protected',
  },
  {
    path: '/assign-pupils',
    element: <AssignPupils />,
    label: 'Assign Pupils',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Classes', 'Assign Pupils'],
    layout: 'protected',
  },
  {
    path: '/subjects/new',
    element: <FeaturePlaceholder title="Create Subject" subtitle="Add a new subject to the school." />,
    label: 'Create Subject',
    roles: ['school_admin'],
    breadcrumb: ['Subjects', 'Create'],
    layout: 'protected',
  },
  {
    path: '/subjects/allocation',
    element: <FeaturePlaceholder title="Subject Allocation" subtitle="Choose which classes take this subject." />,
    label: 'Subject Allocation',
    roles: ['school_admin'],
    breadcrumb: ['Subjects', 'Allocation'],
    layout: 'protected',
  },
  {
    path: '/subjects/:subjectId/analytics',
    element: <FeaturePlaceholder title="Subject Analytics" subtitle="View average marks and grades for this subject." />,
    label: 'Subject Analytics',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Subjects', 'Analytics'],
    layout: 'protected',
  },
  {
    path: '/academic-years',
    element: <FeaturePlaceholder title="Academic Years" subtitle="Set up, open, and close school years." />,
    label: 'Academic Years',
    roles: ['school_admin'],
    breadcrumb: ['Academic Years'],
    layout: 'protected',
  },
  {
    path: '/academic-years/new',
    element: <FeaturePlaceholder title="Create Academic Year" subtitle="Start a new school year." />,
    label: 'Create Academic Year',
    roles: ['school_admin'],
    breadcrumb: ['Academic Years', 'Create'],
    layout: 'protected',
  },
  {
    path: '/terms',
    element: <FeaturePlaceholder title="Terms" subtitle="Set term start/end dates and report options." />,
    label: 'Terms',
    roles: ['school_admin'],
    breadcrumb: ['Terms'],
    layout: 'protected',
  },
  {
    path: '/academic-years/:yearId/activate',
    element: <FeaturePlaceholder title="Activate Academic Year" subtitle="Set the active school year." />,
    label: 'Activate Year',
    roles: ['school_admin'],
    breadcrumb: ['Academic Years', 'Activate'],
    layout: 'protected',
  },
  {
    path: '/academic-years/:yearId/archive',
    element: <FeaturePlaceholder title="Archive Academic Year" subtitle="Close and archive school year records." />,
    label: 'Archive Year',
    roles: ['school_admin'],
    breadcrumb: ['Academic Years', 'Archive'],
    layout: 'protected',
  },
  {
    path: '/subjects/:subjectId/assign',
    element: <FeaturePlaceholder title="Assign Teacher to Subject" subtitle="Assign a teacher to teach this subject." />,
    label: 'Assign Teacher',
    roles: ['school_admin'],
    breadcrumb: ['Subjects', 'Assign Teacher'],
    layout: 'protected',
  },
  {
    path: '/classes/:classId/assign-teacher-subject',
    element: <FeaturePlaceholder title="Assign Teacher to Class" subtitle="Assign teachers to subjects in this class." />,
    label: 'Assign Class Teacher',
    roles: ['school_admin'],
    breadcrumb: ['Classes', 'Assign Teacher'],
    layout: 'protected',
  },
  {
    path: '/assignments/bulk',
    element: <FeaturePlaceholder title="Bulk Assignment" subtitle="Upload class and subject assignments using a file." />,
    label: 'Bulk Assignments',
    roles: ['school_admin'],
    breadcrumb: ['Teachers', 'Bulk Assignments'],
    layout: 'protected',
  },
  {
    path: '/assignments/history',
    element: <FeaturePlaceholder title="Assignment History" subtitle="View past teacher and class schedules." />,
    label: 'Assignment History',
    roles: ['school_admin'],
    breadcrumb: ['Teachers', 'Assignment History'],
    layout: 'protected',
  },
  {
    path: '/class-teachers/assign',
    element: <FeaturePlaceholder title="Assign Class Teacher" subtitle="Assign a teacher to lead a class." />,
    label: 'Assign Advisor',
    roles: ['school_admin'],
    breadcrumb: ['Teachers', 'Assign Advisor'],
    layout: 'protected',
  },
  {
    path: '/class-teachers',
    element: <FeaturePlaceholder title="Current Class Teachers" subtitle="List of all class teachers." />,
    label: 'Current Advisors',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Teachers', 'Current Advisors'],
    layout: 'protected',
  },
  {
    path: '/classes/oversight',
    element: <FeaturePlaceholder title="Class Oversight Dashboard" subtitle="Monitor class attendance and lessons covered." />,
    label: 'Oversight Dashboard',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Classes', 'Oversight'],
    layout: 'protected',
  },
  {
    path: '/results/submitted',
    element: <FeaturePlaceholder title="Submitted Results" subtitle="View grade sheets that have been submitted." />,
    label: 'Submitted Results',
    roles: ['teacher', 'class_teacher', 'headteacher'],
    breadcrumb: ['Results', 'Submitted'],
    layout: 'protected',
  },
  {
    path: '/results/validated',
    element: <FeaturePlaceholder title="Validated Results" subtitle="View grades that are approved and ready to lock." />,
    label: 'Validated Results',
    roles: ['headteacher', 'deputy_headteacher'],
    permissions: ['validate_results'],
    breadcrumb: ['Results', 'Validated'],
    layout: 'protected',
  },
  {
    path: '/results/locked',
    element: <FeaturePlaceholder title="Locked Results" subtitle="View locked grades and past changes." />,
    label: 'Locked Results',
    roles: ['headteacher', 'deputy_headteacher', 'school_admin'],
    permissions: ['lock_results'],
    breadcrumb: ['Results', 'Locked'],
    layout: 'protected',
  },
  {
    path: '/results/upload',
    element: <FeaturePlaceholder title="Bulk Result Upload" subtitle="Upload scores from a spreadsheet." />,
    label: 'Bulk Result Upload',
    roles: ['school_admin', 'teacher'],
    permissions: ['manage_results'],
    breadcrumb: ['Results', 'Upload'],
    layout: 'protected',
  },
  {
    path: '/results/approvals',
    element: <FeaturePlaceholder title="Result Approval Workflow" subtitle="Approve class test and homework grades." />,
    label: 'Approval Workflow',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Results', 'Approvals'],
    layout: 'protected',
  },
  {
    path: '/results/pending-validations',
    element: <FeaturePlaceholder title="Pending Validations" subtitle="Review and approve class grade sheets." />,
    label: 'Pending Validations',
    roles: ['headteacher', 'deputy_headteacher'],
    breadcrumb: ['Results', 'Pending Validations'],
    layout: 'protected',
  },
  {
    path: '/classes/:classId/validate',
    element: <FeaturePlaceholder title="Class Validation" subtitle="Approve grade sheets for this class." />,
    label: 'Class Validation',
    roles: ['headteacher', 'deputy_headteacher', 'class_teacher'],
    breadcrumb: ['Classes', 'Validation'],
    layout: 'protected',
  },
  {
    path: '/results/completion-status',
    element: <FeaturePlaceholder title="Subject Completion Status" subtitle="Check which classes have finished entering marks." />,
    label: 'Completion Status',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Results', 'Completion Status'],
    layout: 'protected',
  },
  {
    path: '/workflow/setup',
    element: <FeaturePlaceholder title="Approval Workflow Settings" subtitle="Set up the grade approval steps." />,
    label: 'Workflow Setup',
    roles: ['school_admin'],
    breadcrumb: ['Workflow', 'Setup'],
    layout: 'protected',
  },
  {
    path: '/results/locking-management',
    element: <FeaturePlaceholder title="Result Locking" subtitle="Lock grades to prevent any more changes." />,
    label: 'Locking Management',
    roles: ['headteacher', 'deputy_headteacher', 'school_admin'],
    breadcrumb: ['Results', 'Locking Management'],
    layout: 'protected',
  },
  {
    path: '/reports/generate-report-cards',
    element: <FeaturePlaceholder title="Generate Report Cards" subtitle="Calculate term grades and create report cards." />,
    label: 'Generate Cards',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher'],
    breadcrumb: ['Reports', 'Generate'],
    layout: 'protected',
  },
  {
    path: '/reports/class',
    element: <FeaturePlaceholder title="Class Reports" subtitle="View class averages and student positions." />,
    label: 'Class Reports',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Reports', 'Class Reports'],
    layout: 'protected',
  },
  {
    path: '/reports/subject',
    element: <FeaturePlaceholder title="Subject Reports" subtitle="View average grades and marks for each subject." />,
    label: 'Subject Reports',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'teacher'],
    breadcrumb: ['Reports', 'Subject Reports'],
    layout: 'protected',
  },
  {
    path: '/reports/rankings',
    element: <FeaturePlaceholder title="Performance Rankings" subtitle="View student positions list by term marks." />,
    label: 'Performance Rankings',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Reports', 'Rankings'],
    layout: 'protected',
  },
  {
    path: '/reports/transcripts',
    element: <FeaturePlaceholder title="Student Transcripts" subtitle="View students\' complete grade history." />,
    label: 'Student Transcripts',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Reports', 'Transcripts'],
    layout: 'protected',
  },
  {
    path: '/reports/printable',
    element: <FeaturePlaceholder title="Printable Reports" subtitle="Download and print report cards." />,
    label: 'Printable Layouts',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Reports', 'Printable'],
    layout: 'protected',
  },
  {
    path: '/reports/pdf-export',
    element: <FeaturePlaceholder title="PDF Export" subtitle="Download all report cards as PDF files." />,
    label: 'PDF Export',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'class_teacher', 'teacher'],
    breadcrumb: ['Reports', 'PDF Export'],
    layout: 'protected',
  },
  {
    path: '/analytics/school',
    element: <FeaturePlaceholder title="School Performance Analytics" subtitle="View student registration and passing trends." />,
    label: 'School Analytics',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Analytics', 'School'],
    layout: 'protected',
  },
  {
    path: '/analytics/subjects',
    element: <FeaturePlaceholder title="Subject Analytics" subtitle="Compare subject averages across different classes." />,
    label: 'Subject Analytics',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Analytics', 'Subjects'],
    layout: 'protected',
  },
  {
    path: '/analytics/teachers',
    element: <FeaturePlaceholder title="Teacher Performance Analytics" subtitle="View teacher activity and student results." />,
    label: 'Teacher Analytics',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Analytics', 'Teachers'],
    layout: 'protected',
  },
  {
    path: '/analytics/rankings',
    element: <FeaturePlaceholder title="Student Rankings" subtitle="View grades trends of top students." />,
    label: 'Student Rankings',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Analytics', 'Rankings'],
    layout: 'protected',
  },
  {
    path: '/analytics/classes',
    element: <FeaturePlaceholder title="Class Comparisons" subtitle="Compare grades between classes." />,
    label: 'Class Comparisons',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Analytics', 'Classes'],
    layout: 'protected',
  },
  {
    path: '/analytics/insights',
    element: <FeaturePlaceholder title="Trends & Insights" subtitle="View automated advice on student grade trends." />,
    label: 'Insights',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher'],
    breadcrumb: ['Analytics', 'Insights'],
    layout: 'protected',
  },
  {
    path: '/notifications',
    element: <FeaturePlaceholder title="Notifications Center" subtitle="View school notices, alerts, and system updates." />,
    label: 'Notifications',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'teacher', 'class_teacher'],
    breadcrumb: ['Notifications'],
    layout: 'protected',
  },
  {
    path: '/settings/school',
    element: <FeaturePlaceholder title="School Profile (Settings)" subtitle="Change school name, phone number, address, and profile." />,
    label: 'School Settings',
    roles: ['school_admin'],
    breadcrumb: ['Settings', 'School'],
    layout: 'protected',
  },
  {
    path: '/settings/branding',
    element: <FeaturePlaceholder title="Branding" subtitle="Change school logo and theme colors." />,
    label: 'Branding',
    roles: ['school_admin'],
    breadcrumb: ['Settings', 'Branding'],
    layout: 'protected',
  },
  {
    path: '/settings/grading',
    element: <Settings />,
    label: 'Grading Rules',
    roles: ['school_admin'],
    breadcrumb: ['Settings', 'Grading'],
    layout: 'protected',
  },
  {
    path: '/settings/results',
    element: <FeaturePlaceholder title="Result Configuration" subtitle="Adjust grade entry deadlines and exam options." />,
    label: 'Result Config',
    roles: ['school_admin'],
    breadcrumb: ['Settings', 'Results'],
    layout: 'protected',
  },
  {
    path: '/settings/preferences',
    element: <FeaturePlaceholder title="User Preferences" subtitle="Change your dashboard colors and notification options." />,
    label: 'Preferences',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'teacher', 'class_teacher'],
    breadcrumb: ['Settings', 'Preferences'],
    layout: 'protected',
  },
  {
    path: '/settings/academics',
    element: <FeaturePlaceholder title="Academic Configuration" subtitle="Set up school calendar and term schedules." />,
    label: 'Academic Setup',
    roles: ['school_admin'],
    breadcrumb: ['Settings', 'Academics'],
    layout: 'protected',
  },
  {
    path: '/profile',
    element: <Profile />,
    label: 'Profile',
    roles: ['school_admin', 'headteacher', 'deputy_headteacher', 'teacher', 'class_teacher'],
    breadcrumb: ['Profile'],
    layout: 'protected',
  },
  {
    path: '/oversight/analytics',
    element: <FeaturePlaceholder title="School Analytics (summary)" subtitle="View overall school results and statistics." />,
    label: 'Analytics Summary',
    roles: ['headteacher', 'deputy_headteacher'],
    breadcrumb: ['Oversight', 'Analytics'],
    layout: 'protected',
  },
  {
    path: '/oversight/teachers',
    element: <FeaturePlaceholder title="Teacher Monitoring" subtitle="Monitor teacher grade entries and deadlines." />,
    label: 'Teacher Monitor',
    roles: ['headteacher', 'deputy_headteacher'],
    breadcrumb: ['Oversight', 'Teachers'],
    layout: 'protected',
  },
  {
    path: '/oversight/validations',
    element: <FeaturePlaceholder title="Result Validation (oversight)" subtitle="Approve and sign off completed grade sheets." />,
    label: 'Oversight Validations',
    roles: ['headteacher', 'deputy_headteacher'],
    breadcrumb: ['Oversight', 'Validations'],
    layout: 'protected',
  },
  {
    path: '/oversight/reports',
    element: <FeaturePlaceholder title="Reports (school-level)" subtitle="View school-wide grade averages and reports." />,
    label: 'Oversight Reports',
    roles: ['headteacher', 'deputy_headteacher'],
    breadcrumb: ['Oversight', 'Reports'],
    layout: 'protected',
  },
  {
    path: '/oversight/performance',
    element: <FeaturePlaceholder title="Academic Performance" subtitle="Review student failure lists and top scorers." />,
    label: 'Oversight Performance',
    roles: ['headteacher', 'deputy_headteacher'],
    breadcrumb: ['Oversight', 'Performance'],
    layout: 'protected',
  },
  {
    path: '/results/my-submitted',
    element: <FeaturePlaceholder title="Submitted Results (teacher view)" subtitle="View submitted grade sheets waiting for approval." />,
    label: 'My Submissions',
    roles: ['teacher', 'class_teacher'],
    breadcrumb: ['Results', 'My Submissions'],
    layout: 'protected',
  },
  {
    path: '/class-teacher/dashboard',
    element: <FeaturePlaceholder title="Class Teacher Responsibilities" subtitle="Overview for Class Teachers." />,
    label: 'Class Advisor Hub',
    roles: ['class_teacher'],
    breadcrumb: ['Class Advisor', 'Dashboard'],
    layout: 'protected',
  },
  {
    path: '/class-teacher/overview',
    element: <FeaturePlaceholder title="Class Overview" subtitle="View your class list, attendance, and grade averages." />,
    label: 'Advisor Roster',
    roles: ['class_teacher'],
    breadcrumb: ['Class Advisor', 'Overview'],
    layout: 'protected',
  },
  {
    path: '/class-teacher/validation',
    element: <FeaturePlaceholder title="Result Validation (for own class)" subtitle="Check and approve your class grades before sending to Headteacher." />,
    label: 'Advisor Validation',
    roles: ['class_teacher'],
    breadcrumb: ['Class Advisor', 'Validation'],
    layout: 'protected',
  },
  {
    path: '/class-teacher/performance',
    element: <FeaturePlaceholder title="Class Performance" subtitle="View grade charts for your class." />,
    label: 'Advisor Analytics',
    roles: ['class_teacher'],
    breadcrumb: ['Class Advisor', 'Performance'],
    layout: 'protected',
  },
  {
    path: '/class-teacher/comments',
    element: <FeaturePlaceholder title="Student Comments" subtitle="Write class teacher comments for report cards." />,
    label: 'Advisor Comments',
    roles: ['class_teacher'],
    breadcrumb: ['Class Advisor', 'Comments'],
    layout: 'protected',
  },
  {
    path: '/class-teacher/finalize',
    element: <FeaturePlaceholder title="Report Finalization" subtitle="Approve and finalize report cards for your class." />,
    label: 'Advisor Finalize',
    roles: ['class_teacher'],
    breadcrumb: ['Class Advisor', 'Finalize'],
    layout: 'protected',
  },
];

interface RoleGuardProps {
  element: React.ReactElement;
  allowedRoles?: string[];
}

const RoleGuard: React.FC<RoleGuardProps> = ({ element, allowedRoles }) => {
  const { user, isAuthenticated, loading } = useAuth();

  // Superadmin bypass / validation check
  if (allowedRoles && allowedRoles.includes('super_admin')) {
    const saToken = localStorage.getItem('superadminToken');
    if (!saToken) {
      return <Navigate to="/login" replace />;
    }
    return element;
  }

  if (loading) {
    return (
      <div className="flex h-screen w-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-4 border-indigo-600 border-t-transparent mx-auto"></div>
          <p className="mt-4 text-xs font-semibold text-slate-500">Verifying permissions...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const userRoles = user?.roles || [];
    const hasAccess = allowedRoles.some((role) => userRoles.includes(role));
    if (!hasAccess) {
      return <Navigate to="/" replace />;
    }
  }

  return element;
};

export const renderRouteElements = (routes: PageRoute[]): React.ReactNode =>
  routes.map((route) => {
    if (route.children && route.children.length > 0) {
      return (
        <Route 
          key={route.path} 
          path={route.path} 
          element={<RoleGuard element={route.element} allowedRoles={route.roles} />}
        >
          {renderRouteElements(route.children)}
        </Route>
      );
    }
    
    return (
      <Route 
        key={route.path} 
        path={route.path} 
        element={<RoleGuard element={route.element} allowedRoles={route.roles} />} 
      />
    );
  });
